import { useCallback, useEffect, useState } from "react";
import { Books, Sparkle, MagnifyingGlass, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState } from "@/components/Shared";
import { ShareBar } from "@/components/ShareBar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

const oops = (e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail));
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const LABEL = (s) => (s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
const SORT_LABEL = { name: "A–Z", price_low: "Cheapest", price_high: "Dearest",
  stock_low: "Least stock", stock_high: "Most stock", newest: "Newest" };
const PUBLIC_KINDS = ["listing", "fashion", "ad"];

export default function Catalogue() {
  const { user } = useAuth();
  const [view, setView] = useState("lines");
  const [data, setData] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [kind, setKind] = useState("");
  const [category, setCategory] = useState("");
  const [sort, setSort] = useState("name");
  const [q, setQ] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/catalogue", { params: { kind: kind || undefined, category: category || undefined, sort, q: q || undefined } })
      .then(({ data: d }) => setData(d)).catch(oops);
  }, [kind, category, sort, q]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (view === "analysis" && !analysis) {
      api.get("/catalogue/analysis").then(({ data: d }) => setAnalysis(d)).catch(oops);
    }
  }, [view, analysis]);

  const refresh = async () => {
    setBusy(true);
    try {
      const { data: d } = await api.post("/catalogue/refresh");
      setAnalysis(d.analysis);
      toast.success("Catalogue read again by AI");
    } catch (e) { oops(e); } finally { setBusy(false); }
  };

  const addCollection = async () => {
    try {
      await api.post("/catalogue/collections", { name });
      setName(""); toast.success("Collection added"); load();
    } catch (e) { oops(e); }
  };

  const removeCollection = async (id) => {
    try { await api.delete(`/catalogue/collections/${id}`); load(); } catch (e) { oops(e); }
  };

  if (!data) return <p className="py-10 text-center text-sm text-slate-500">Loading the catalogue…</p>;

  return (
    <div className="space-y-5" data-testid="catalogue-page">
      <PageHeader overline="What we sell" title="Catalogue" testid="catalogue-header"
        description="Stock, listings, apparel and adverts in one place — searchable, sortable, and read again by AI every morning." />

      <div className="flex flex-wrap items-center gap-2">
        <a href="/catalogue" target="_blank" rel="noreferrer" data-testid="catalogue-public-link"
          className="tap-safe border border-border px-3 py-2 text-xs font-semibold text-slate-600 hover:border-[#002FA7] hover:text-[#002FA7]">
          Public catalogue
        </a>
        <ShareBar url={`${BACKEND}/api/share/catalogue`} text="Our catalogue"
          testid="catalogue-share" compact instagram />
        {user?.role === "admin" && (
          <Button variant="outline" data-testid="catalogue-refresh" onClick={refresh} disabled={busy}>
            <Sparkle size={15} className="mr-1.5" /> {busy ? "Reading…" : "Read it again"}
          </Button>
        )}
      </div>

      <Tabs value={view} onValueChange={setView}>
        <TabsList className="scroll-x w-full justify-start overflow-x-auto" data-testid="catalogue-tabs">
          <TabsTrigger value="lines" data-testid="catalogue-tab-lines">Every line ({data.count})</TabsTrigger>
          <TabsTrigger value="analysis" data-testid="catalogue-tab-analysis">The read</TabsTrigger>
          <TabsTrigger value="collections" data-testid="catalogue-tab-collections">Collections</TabsTrigger>
        </TabsList>
      </Tabs>

      {view === "lines" && (
        <>
          <div className="grid gap-2 sm:grid-cols-4">
            <div className="relative sm:col-span-2">
              <MagnifyingGlass size={14} className="absolute left-2.5 top-3 text-slate-400" />
              <Input data-testid="catalogue-search" className="pl-8" placeholder="Search the catalogue"
                value={q} onChange={(e) => setQ(e.target.value)} />
            </div>
            <select data-testid="catalogue-kind" className="border border-border bg-white px-2 py-2 text-base md:text-sm"
              value={kind} onChange={(e) => setKind(e.target.value)}>
              <option value="">Everything</option>
              {data.kinds.map((k) => <option key={k.key} value={k.key}>{k.label}</option>)}
            </select>
            <select data-testid="catalogue-sort" className="border border-border bg-white px-2 py-2 text-base md:text-sm"
              value={sort} onChange={(e) => setSort(e.target.value)}>
              {data.sorts.map((s) => <option key={s} value={s}>{SORT_LABEL[s] || s}</option>)}
            </select>
          </div>

          <div className="scroll-x flex gap-1.5 overflow-x-auto" data-testid="catalogue-categories">
            {[["", "All categories"], ...data.categories.map((c) => [c, LABEL(c)])].map(([key, label]) => (
              <button key={key || "all"} data-testid={`catalogue-category-${key || "all"}`} onClick={() => setCategory(key)}
                className={`tap-safe shrink-0 border px-2.5 py-1.5 text-xs font-semibold transition-colors duration-150 ${
                  category === key ? "border-[#0F172A] bg-[#0F172A] text-white"
                    : "border-border bg-white text-slate-600 hover:border-[#0F172A] hover:text-[#0F172A]"}`}>
                {label}
              </button>
            ))}
          </div>

          {data.entries.length === 0 ? (
            <EmptyState testid="catalogue-empty" title="Nothing matches"
              description="Try another category, or clear the search." />
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="catalogue-entries">
              {data.entries.map((e, i) => (
                <div key={`${e.kind}-${e.id}`} data-testid={`catalogue-entry-${i}`} className="border border-border bg-white">
                  {e.image_url && <img src={`${BACKEND}${e.image_url}`} alt={e.name} className="h-36 w-full object-cover" />}
                  <div className="p-3">
                    <p className="text-[11px] uppercase tracking-wide text-slate-500">
                      {e.kind_label}{e.category ? ` · ${LABEL(e.category)}` : ""}
                    </p>
                    <p className="mt-0.5 font-semibold text-[#0F172A]">{e.name}</p>
                    <p className="mt-1 text-sm text-slate-600">
                      {e.price != null ? `${e.symbol}${e.price}` : "no price yet"}
                      {e.quantity != null ? ` · ${e.quantity} ${e.unit || ""}` : ""}
                    </p>
                    {PUBLIC_KINDS.includes(e.kind) && e.price != null && (
                      <div className="mt-2">
                        <ShareBar url={`${BACKEND}/api/share/catalogue/${e.kind}/${e.id}`} text={e.name}
                          testid={`catalogue-share-${i}`} compact instagram />
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {view === "analysis" && (
        analysis ? (
          <div className="space-y-4" data-testid="catalogue-analysis">
            <div className="grid gap-3 sm:grid-cols-4">
              {[["Lines", analysis.numbers.lines], ["Priced", analysis.numbers.priced_lines],
                ["Average price", analysis.numbers.average_price],
                ["Stock value", analysis.numbers.stock_value]].map(([label, value]) => (
                <div key={label} className="border border-border bg-white p-3">
                  <p className="overline text-slate-500">{label}</p>
                  <p className="mt-1 text-xl font-bold text-[#0F172A]">{value}</p>
                </div>
              ))}
            </div>
            {analysis.read && (
              <div className="border-l-2 border-[#002FA7] bg-[#EEF3FF] px-4 py-3" data-testid="catalogue-read">
                <p className="overline text-[#002FA7]">The read · {analysis.day}</p>
                <p className="mt-1 whitespace-pre-line text-base leading-relaxed text-[#0F172A]">{analysis.read}</p>
                <p className="mt-2 text-[11px] uppercase tracking-wide text-slate-500">By {analysis.engine_label}</p>
              </div>
            )}
            <div className="grid gap-3 lg:grid-cols-3">
              {[["Push these", analysis.push, "#0B5C2E"], ["Drop or discount", analysis.drop, "#8E1C15"]].map(([title, list, tone]) => (
                <div key={title} className="border border-border bg-white p-4">
                  <p className="text-sm font-semibold" style={{ color: tone }}>{title}</p>
                  <ul className="mt-2 space-y-1 text-sm text-slate-700">
                    {(list || []).map((x, n) => <li key={n}>· {x}</li>)}
                    {(list || []).length === 0 && <li className="text-slate-400">Nothing flagged</li>}
                  </ul>
                </div>
              ))}
              <div className="border border-border bg-white p-4" data-testid="catalogue-pricing">
                <p className="text-sm font-semibold text-[#0F172A]">Pricing by market</p>
                <ul className="mt-2 space-y-1 text-sm text-slate-700">
                  {(analysis.pricing || []).map((p, n) => <li key={n}>{p.market}: {p.advice}</li>)}
                  {(analysis.pricing || []).length === 0 && <li className="text-slate-400">No advice yet</li>}
                </ul>
              </div>
            </div>
            <div className="grid gap-3 lg:grid-cols-3">
              {[["Moving", analysis.movers, (r) => `${r.name} · ${r.units}`],
                ["Not moving", analysis.dead_stock, (r) => `${r.name} · ${r.quantity ?? 0} left`],
                ["Thin margin", analysis.thin_margin, (r) => `${r.name} · ${r.margin_pct}%`]].map(([title, list, fmt]) => (
                <div key={title} className="border border-border bg-white p-4">
                  <p className="text-sm font-semibold text-[#0F172A]">{title}</p>
                  <ul className="mt-2 space-y-1 text-sm text-slate-700">
                    {(list || []).slice(0, 8).map((r, n) => <li key={n}>{fmt(r)}</li>)}
                    {(list || []).length === 0 && <li className="text-slate-400">Nothing here</li>}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        ) : <p className="py-8 text-center text-sm text-slate-500">Reading the catalogue…</p>
      )}

      {view === "collections" && (
        <div className="space-y-3" data-testid="catalogue-collections">
          {data.can_manage && (
            <div className="flex flex-wrap items-end gap-2 border border-border bg-white p-4">
              <Input data-testid="catalogue-collection-name" className="w-full sm:w-72"
                placeholder="Winter site kit" value={name} onChange={(e) => setName(e.target.value)} />
              <Button data-testid="catalogue-collection-add" onClick={addCollection} disabled={name.trim().length < 2}>
                Add collection
              </Button>
            </div>
          )}
          {data.collections.length === 0 ? (
            <EmptyState testid="catalogue-collections-empty" title="No collections yet"
              description="Group lines into named collections like 'Winter site kit' to share them as one." />
          ) : (
            <ul className="space-y-2">
              {data.collections.map((c) => (
                <li key={c.id} data-testid={`catalogue-collection-${c.id}`}
                  className="flex flex-wrap items-center justify-between gap-2 border border-border bg-white p-3 text-sm">
                  <span className="inline-flex items-center gap-1.5 font-semibold text-[#0F172A]">
                    <Books size={15} /> {c.name}
                  </span>
                  {user?.role === "admin" && (
                    <button data-testid={`catalogue-collection-delete-${c.id}`} onClick={() => removeCollection(c.id)}
                      className="tap-safe inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-[#B3241B]">
                      <Trash size={13} /> Remove
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
